import { useEffect, useMemo, useState } from "react";
import cpApi from "@/lib/clientPortalApi";
import { PageHeader, fmtDate, StatusPill } from "./_cpShared";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function CpCompliance() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await cpApi.get("/client-portal/compliance", { params: { year, month } });
        if (active) setRows(r.data || []);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [year, month]);

  // Build calendar grid (start on Monday)
  const grid = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1);
    const daysInMonth = new Date(year, month, 0).getDate();
    // Mon=0..Sun=6
    const startOffset = (firstDay.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  const byDay = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      if (!r.due_date) return;
      const day = parseInt(String(r.due_date).slice(8, 10), 10);
      if (!m[day]) m[day] = [];
      m[day].push(r);
    });
    return m;
  }, [rows]);

  const prevMonth = () => {
    if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1);
  };

  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  return (
    <div>
      <PageHeader subtitle="Statutory due dates" title="Compliance Calendar" />
      <div className="p-4 sm:p-8 space-y-4">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-1.5 rounded-sm border border-slate-300 hover:bg-slate-50" data-testid="cp-cal-prev"><ChevronLeft size={14} /></button>
          <div className="text-sm font-semibold min-w-[140px] text-center" style={{ fontFamily: "Outfit" }} data-testid="cp-cal-label">
            {MONTHS[month - 1]} {year}
          </div>
          <button onClick={nextMonth} className="p-1.5 rounded-sm border border-slate-300 hover:bg-slate-50" data-testid="cp-cal-next"><ChevronRight size={14} /></button>
          <div className="text-xs text-slate-500 ml-3">{rows.length} item{rows.length === 1 ? "" : "s"} this month</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="cp-compliance-calendar">
          <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="px-2 py-2 text-[10px] tracking-[0.15em] uppercase text-slate-500 text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {loading && <div className="col-span-7 p-12 flex justify-center"><Loader2 className="animate-spin" /></div>}
            {!loading && grid.map((day, idx) => {
              const items = day ? byDay[day] || [] : [];
              const isToday = day && `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}` === todayKey;
              return (
                <div
                  key={idx}
                  className={`min-h-[100px] border-r border-b border-slate-100 p-2 ${day ? "" : "bg-slate-50/50"}`}
                  data-testid={day ? `cp-cal-day-${day}` : undefined}
                >
                  {day && (
                    <>
                      <div className={`text-[11px] mb-1 ${isToday ? "font-bold text-[#0a2540]" : "text-slate-500"}`}>
                        {day} {isToday && <span className="text-[9px] uppercase tracking-wider ml-1 bg-[#0a2540] text-white px-1 rounded-sm">Today</span>}
                      </div>
                      <div className="space-y-1">
                        {items.map((it) => (
                          <div key={it.id} className={`text-[10px] px-1.5 py-0.5 rounded-sm truncate ${it.status === "Done" ? "bg-emerald-100 text-emerald-800" : it.status === "Overdue" ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"}`}
                               title={`${it.title} (${it.category})`}>
                            {it.title}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* List view */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Title</Th>
                <Th>Category</Th>
                <Th>Due date</Th>
                <Th>Status</Th>
                <Th>Description</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <Td className="font-semibold">{r.title}</Td>
                  <Td>{r.category || "—"}</Td>
                  <Td>{fmtDate(r.due_date)}</Td>
                  <Td><StatusPill status={r.status} /></Td>
                  <Td className="text-slate-500">{r.description || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-3 py-2.5 text-[10px] tracking-wide uppercase text-slate-500">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 ${className}`}>{children}</td>;
